import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ShieldCheck, Link as LinkIcon, Loader2, Copy, Ban, Search,
    FileText, Eye, Timer, ExternalLink, X, ChevronDown, ChevronRight,
    UserRound,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function fmtFecha(iso) {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
}
function fmtIP(ip) {
    if (!ip) return "—";
    if (ip.split(".").length === 4) {
        const [a, b] = ip.split(".");
        return `${a}.${b}.***.***`;
    }
    return ip.slice(0, 15) + "…";
}

function CrearShareDialog({ documentos, onCreated }) {
    const [open, setOpen] = useState(false);
    const [docId, setDocId] = useState("");
    const [expiresHours, setExpiresHours] = useState("48");
    const [maxDl, setMaxDl] = useState("");
    const [viewerEmail, setViewerEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setDocId(""); setExpiresHours("48"); setMaxDl(""); setViewerEmail("");
        setResult(null);
    };

    const submit = async () => {
        if (!docId) return toast.error("Selecciona un documento");
        setBusy(true);
        try {
            const payload = { expires_hours: Number(expiresHours) || 48 };
            if (maxDl) payload.max_downloads = Number(maxDl);
            if (viewerEmail) payload.viewer_email = viewerEmail;
            const { data } = await api.post(`/documentos/${docId}/share`, payload);
            toast.success("Share creado");
            setResult(data);
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(result?.url_publico || "");
        toast.success("URL copiada al portapapeles");
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button data-testid="crear-share-btn">
                    <LinkIcon className="h-4 w-4 mr-1" /> Crear share link
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Compartir documento con lender</DialogTitle>
                </DialogHeader>
                {!result ? (
                    <div className="space-y-4">
                        <p className="text-xs text-muted-foreground">
                            El enlace genera un PDF con watermark dinámico (gold Trafalgar)
                            incrustado en cada página. Cada acceso queda registrado en el
                            access log.
                        </p>
                        <div>
                            <Label>Documento *</Label>
                            <Select value={docId} onValueChange={setDocId}>
                                <SelectTrigger className="mt-1" data-testid="share-doc-select">
                                    <SelectValue placeholder="Selecciona un documento…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {documentos.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.nombre?.slice(0, 60)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Horas de vigencia</Label>
                                <Input type="number" min={1} max={720}
                                    value={expiresHours}
                                    onChange={(e) => setExpiresHours(e.target.value)}
                                    className="mt-1" data-testid="share-hours" />
                            </div>
                            <div>
                                <Label>Max downloads (opcional)</Label>
                                <Input type="number" min={1} max={200}
                                    placeholder="Sin límite"
                                    value={maxDl}
                                    onChange={(e) => setMaxDl(e.target.value)}
                                    className="mt-1" data-testid="share-maxdl" />
                            </div>
                        </div>
                        <div>
                            <Label>Email del viewer (para watermark)</Label>
                            <Input type="email"
                                placeholder="analyst@lender-acme.com"
                                value={viewerEmail}
                                onChange={(e) => setViewerEmail(e.target.value)}
                                className="mt-1" data-testid="share-viewer-email" />
                            <div className="text-[10px] text-muted-foreground mt-1">
                                Aparecerá en cada página del PDF con timestamp.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="rounded-md border border-[color:var(--brand-exito)]/40 bg-[color:var(--brand-exito)]/5 p-4"
                            data-testid="share-result">
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck className="h-4 w-4 text-[color:var(--brand-exito)]" />
                                <span className="text-sm font-medium">Share creado</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                URL público (JWT firmado)
                            </div>
                            <div className="flex items-center gap-2">
                                <code className="text-[10px] flex-1 bg-muted p-2 rounded-sm break-all">
                                    {result.url_publico}
                                </code>
                                <Button size="sm" variant="ghost" onClick={copyLink}
                                    data-testid="share-copy-url">
                                    <Copy className="h-3 w-3" />
                                </Button>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span className="text-muted-foreground">Vigencia:</span>{" "}
                                    {fmtFecha(result.expires_at)}
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Max downloads:</span>{" "}
                                    {result.max_downloads || "Sin límite"}
                                </div>
                            </div>
                            <div className="mt-2 text-[10px] text-muted-foreground italic">
                                Watermark: {result.watermark_text}
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    {!result ? (
                        <>
                            <Button variant="outline" onClick={() => setOpen(false)}
                                disabled={busy}>Cancelar</Button>
                            <Button onClick={submit} disabled={busy}
                                data-testid="share-submit">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                      : "Crear share"}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => setOpen(false)}
                            data-testid="share-done-btn">
                            Cerrar
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AccessLogDrawer({ documentoId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/documentos/${documentoId}/access-log`);
                setData(data);
            } catch { toast.error("Error cargando access log"); }
            finally { setLoading(false); }
        })();
    }, [documentoId]);
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            onClick={onClose} data-testid="access-log-drawer">
            <div className="w-full max-w-2xl bg-background h-full overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-2xl flex items-center gap-2">
                        <Eye className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Access log
                    </h3>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                        Cargando…
                    </div>
                ) : !data?.shares?.length ? (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                        Sin shares para este documento.
                    </div>
                ) : (
                    <div className="space-y-6">
                        {data.shares.map(({ share, accesos }) => (
                            <div key={share.id} className="rounded-md border border-border p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-medium">
                                            {share.viewer_email || "Sin viewer email"}
                                        </div>
                                        <div className="text-[10px] tabular text-muted-foreground mt-1">
                                            Vigencia: {fmtFecha(share.expires_at)}
                                            {" · "}
                                            Downloads: {share.downloads_count}
                                            {share.max_downloads ? ` / ${share.max_downloads}` : " / ∞"}
                                        </div>
                                    </div>
                                    <span className={share.revocado ? "badge-error" : "badge-exito"}>
                                        {share.revocado ? "Revocado" : "Activo"}
                                    </span>
                                </div>
                                {accesos.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-border/60 space-y-1">
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                            Accesos ({accesos.length})
                                        </div>
                                        {accesos.slice(0, 10).map((a, i) => (
                                            <div key={i} className="flex items-center gap-2 text-[11px] py-1 border-b border-border/30">
                                                <Timer className="h-3 w-3 text-muted-foreground" />
                                                <span className="tabular text-muted-foreground">
                                                    {fmtFecha(a.accedido_at)}
                                                </span>
                                                <span className="tabular">{fmtIP(a.ip)}</span>
                                                <span className="text-muted-foreground truncate flex-1">
                                                    {a.user_agent?.slice(0, 50)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function DataRoom() {
    const { hasRole } = useAuth();
    const [shares, setShares] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [fideicomisos, setFideicomisos] = useState([]);
    const [selFid, setSelFid] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    const [accessLogFor, setAccessLogFor] = useState(null);
    const [revocarId, setRevocarId] = useState(null);
    const [motivoRev, setMotivoRev] = useState("");

    const canCreate = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.ADMIN_TI,
    ]);

    const loadFid = async () => {
        const { data } = await api.get("/fideicomisos");
        setFideicomisos(data);
        if (data.length && !selFid) setSelFid(data[0].id);
    };
    const loadShares = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/data-room/shares");
            setShares(data);
        } catch { toast.error("Error cargando shares"); }
        finally { setLoading(false); }
    };
    const loadDocs = async () => {
        if (!selFid) return;
        try {
            const { data } = await api.get(`/fideicomisos/${selFid}/documentos`);
            setDocumentos(data);
        } catch { setDocumentos([]); }
    };

    useEffect(() => { loadFid(); loadShares(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [selFid]);

    const filtered = useMemo(() => {
        let list = shares;
        if (query) {
            const q = query.toLowerCase();
            list = list.filter((s) =>
                (s.documento_nombre || "").toLowerCase().includes(q) ||
                (s.viewer_email || "").toLowerCase().includes(q) ||
                (s.created_by || "").toLowerCase().includes(q));
        }
        return list;
    }, [shares, query]);

    const kpis = useMemo(() => {
        const activos = shares.filter((s) => !s.revocado).length;
        const revocados = shares.filter((s) => s.revocado).length;
        const totalDl = shares.reduce((sum, s) => sum + (s.downloads_count || 0), 0);
        return { activos, revocados, totalDl, total: shares.length };
    }, [shares]);

    const revocar = async () => {
        setBusy(true);
        try {
            await api.post(`/documentos/shares/${revocarId}/revocar`,
                { motivo: motivoRev || null });
            toast.success("Share revocado");
            setRevocarId(null); setMotivoRev("");
            loadShares();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="data-room-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        A6 · Data Room seguro
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <FileText className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Data Room
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Comparte documentos con lenders vía enlaces JWT firmados con
                        watermark dinámico. Cada acceso queda registrado. Revocable
                        en cualquier momento.
                    </p>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-3">
                        <Select value={selFid} onValueChange={setSelFid}>
                            <SelectTrigger className="w-64" data-testid="dr-fid-select">
                                <SelectValue placeholder="Fideicomiso…" />
                            </SelectTrigger>
                            <SelectContent>
                                {fideicomisos.map((f) => (
                                    <SelectItem key={f.id} value={f.id}>
                                        {f.numero_interno}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <CrearShareDialog
                            documentos={documentos}
                            onCreated={() => loadShares()}
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dr-kpis">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total shares</div>
                    <div className="font-display text-2xl mt-1">{kpis.total}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Activos</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--brand-exito)]">
                        {kpis.activos}
                    </div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revocados</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--brand-coral)]">
                        {kpis.revocados}
                    </div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Descargas totales</div>
                    <div className="font-display text-2xl mt-1">{kpis.totalDl}</div>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por documento, viewer o creador…"
                    value={query} onChange={(e) => setQuery(e.target.value)}
                    className="max-w-md" data-testid="dr-search" />
            </div>

            {loading ? (
                <div className="p-10 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando shares…
                </div>
            ) : (
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <table className="trf-table" data-testid="dr-shares-table">
                        <thead>
                            <tr>
                                <th>Documento</th>
                                <th>Viewer</th>
                                <th>Creado por</th>
                                <th>Vigencia</th>
                                <th>Descargas</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => (
                                <tr key={s.id} data-testid={`dr-share-row-${s.id}`}>
                                    <td className="max-w-xs truncate" title={s.documento_nombre}>
                                        {s.documento_nombre}
                                    </td>
                                    <td className="text-xs">
                                        <div className="flex items-center gap-1">
                                            <UserRound className="h-3 w-3 text-muted-foreground" />
                                            {s.viewer_email || "—"}
                                        </div>
                                    </td>
                                    <td className="text-xs tabular text-muted-foreground">
                                        {s.created_by?.split("@")[0]}
                                        <br />
                                        <span className="text-[10px]">{fmtFecha(s.created_at)}</span>
                                    </td>
                                    <td className="text-xs tabular">{fmtFecha(s.expires_at)}</td>
                                    <td className="text-xs tabular">
                                        {s.downloads_count}
                                        {s.max_downloads ? ` / ${s.max_downloads}` : " / ∞"}
                                    </td>
                                    <td>
                                        {s.revocado ? (
                                            <span className="badge-error">Revocado</span>
                                        ) : new Date(s.expires_at) < new Date() ? (
                                            <span className="badge-mock">Expirado</span>
                                        ) : (
                                            <span className="badge-exito">Activo</span>
                                        )}
                                    </td>
                                    <td className="flex gap-1">
                                        <Button size="sm" variant="ghost"
                                            onClick={() => setAccessLogFor(s.documento_id)}
                                            data-testid={`dr-log-${s.id}`}>
                                            <Eye className="h-3 w-3 mr-1" /> Log
                                        </Button>
                                        {canCreate && !s.revocado && (
                                            <Button size="sm" variant="ghost"
                                                onClick={() => setRevocarId(s.id)}
                                                data-testid={`dr-revocar-${s.id}`}>
                                                <Ban className="h-3 w-3 mr-1" /> Revocar
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                                    Sin shares. {canCreate && "Crea el primero con el botón arriba."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {accessLogFor && (
                <AccessLogDrawer
                    documentoId={accessLogFor}
                    onClose={() => setAccessLogFor(null)}
                />
            )}

            <Dialog open={!!revocarId} onOpenChange={(v) => !v && setRevocarId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Revocar share</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            El enlace dejará de funcionar inmediatamente. Los accesos
                            futuros recibirán 403. Esta acción queda en bitácora.
                        </p>
                        <div>
                            <Label>Motivo (opcional)</Label>
                            <Input value={motivoRev}
                                onChange={(e) => setMotivoRev(e.target.value)}
                                placeholder="Ej. Facility cerrado / cambio de lender"
                                className="mt-1" data-testid="dr-motivo-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRevocarId(null)}>
                            Cancelar
                        </Button>
                        <Button onClick={revocar} disabled={busy}
                            data-testid="dr-revocar-confirm">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Revocar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
